import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LONG_PRESS_MOVE_TOLERANCE_PX } from "@/hooks/useLongPress";

interface Props {
  /** Cursor coordinates (viewport-relative, e.g. `e.clientX` / `e.clientY`). */
  x: number;
  y: number;
  onClose: () => void;
  /** Fixed menu width used both for layout and viewport edge-flipping. */
  width?: number;
  testId?: string;
  children: ReactNode;
}

const EDGE_PAD = 8;

/**
 * Cursor-anchored popover for right-click context menus. Renders `position: fixed`
 * at (x, y), flips inward when it would overflow the viewport, and closes on
 * outside mousedown, Escape, or a touch dragged away from (x, y) — a long-press
 * opens it mid-gesture, and moving on from there is a drag, not a menu pick. Portalled to `document.body` so a transformed
 * ancestor (e.g. the slid-in sidebar) can't reparent its fixed positioning.
 *
 * Styling matches the shared `.details-popover` / `.details-popover-item` classes —
 * fill it with `<button className="details-popover-item">` rows.
 */
export default function ContextMenu({ x, y, onClose, width = 200, testId, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + rect.width > vw - EDGE_PAD) left = Math.max(EDGE_PAD, vw - rect.width - EDGE_PAD);
    if (top + rect.height > vh - EDGE_PAD) top = Math.max(EDGE_PAD, vh - rect.height - EDGE_PAD);
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onTouchMove(e: TouchEvent) {
      const t = e.touches[0];
      if (!t || ref.current?.contains(e.target as Node)) return;
      if (Math.abs(t.clientX - x) > LONG_PRESS_MOVE_TOLERANCE_PX || Math.abs(t.clientY - y) > LONG_PRESS_MOVE_TOLERANCE_PX) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    document.addEventListener("touchmove", onTouchMove);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [onClose, x, y]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={ref}
      className="details-popover"
      style={{ position: "fixed", left: pos.left, top: pos.top, right: "auto", width, zIndex: 50 }}
      data-testid={testId}
      role="menu"
    >
      {children}
    </div>,
    document.body,
  );
}
